import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import React, { FC, useEffect, useState, useContext } from 'react';
import * as yup from 'yup';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import { AddressDeliveryType } from '../../../models/address-delivery-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { OrdersListingType } from '../../../models/orders-listing-type';
import addressBranch from '../../../services/customer-api/endpoints/address/branch';
import FormDatepicker from '../../atoms/form-datepicker/form-datepicker';
import FormSelect from '../../atoms/form-select/form-select';
import FormText from '../../atoms/form-text/form-text';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';

interface Props extends Partial<OrdersListingType> {
  readonly initialDate: Date;
  readonly onAddressChange: (address: number) => void;
  readonly onDateChange: (date: Date) => void;
  readonly onSearchIconClick: () => void;
  readonly onSearchTermChange: (searchTerm: string) => void;
  readonly onStatusChange: (status: number) => void;
}

const OrdersListingFilters: FC<Props> = ({
  filterByAddressFieldLabel,
  filterByAddressPlaceholder,
  filterByMonthLabel,
  filterByMonthPlaceholder,
  filterByStatusAllLabel,
  filterByStatusCompleteLabel,
  filterByStatusFieldLabel,
  filterByStatusOutstandingLabel,
  initialDate,
  onAddressChange,
  onDateChange,
  onSearchIconClick,
  onSearchTermChange,
  onStatusChange,
  searchOrdersFieldLabel,
  searchOrdersFieldPlaceholderText,
}: Props) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const getSessionId = useGetSessionId();
  const [addresses, setAddresses] = useState<AddressDeliveryType[]>();

  useEffect(() => {
    getSessionId().then((sessionId) =>
      addressBranch(sessionId).then((response: AddressDeliveryType[]) => {
        setAddresses(response);
      })
    );
  }, []);

  return (
    <Formik
      initialValues={{
        status: '0',
        address: '0',
        month: initialDate,
        searchTerm: '',
      }}
      validationSchema={yup.object({
        status: yup.string().required().label(filterByStatusFieldLabel),
        address: yup.string().required().label(filterByAddressFieldLabel),
        searchTerm: yup.string().label(searchOrdersFieldLabel),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={null}
    >
      <Form className="grid xl:flex flex-col lg:flex-wrap xl:flex-nowrap gap-4 lg:gap-x-2 2xl:gap-x-4 xl:gap-y-4 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full xl:w-4/12">
            <FormDatepicker
              dateFormat="MM/yyyy"
              endDate={new Date(dayjs(initialDate).add(1, 'd').toISOString())}
              label={filterByMonthLabel}
              name="month"
              onChange={(value: Date) => onDateChange(value)}
              placeholder={filterByMonthPlaceholder}
              showMonthYearPicker
            />
          </div>

          <div className="w-full xl:w-4/12">
            <FormText
              label={searchOrdersFieldLabel}
              onChange={(value: string) => onSearchTermChange(value)}
              name="searchTerm"
              placeholder={searchOrdersFieldPlaceholderText}
              type="search"
              onIconClick={() => onSearchIconClick()}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 flex-grow">
          <div className="w-full xl:w-4/12">
            <FormSelect
              label={filterByStatusFieldLabel}
              name="status"
              onChange={(value: string) => {
                onStatusChange(parseInt(value, 10));

                pushToDataLayer(GtmEventNameEnum.OrdersFilterStatus);
              }}
              options={[
                { label: filterByStatusAllLabel, value: '0' },
                {
                  label: filterByStatusOutstandingLabel,
                  value: '1',
                },
                {
                  label: filterByStatusCompleteLabel,
                  value: '2',
                },
              ]}
            />
          </div>

          <div className="w-full xl:w-4/12">
            <FormSelect
              label={filterByAddressFieldLabel}
              name="address"
              onChange={(value: string) => {
                onAddressChange(parseInt(value, 10));

                pushToDataLayer(GtmEventNameEnum.OrdersFilterAddress);
              }}
              options={[
                { label: filterByStatusAllLabel, value: '0' },
                ...(addresses?.map((address: AddressDeliveryType) => ({
                  label: address.name,
                  value: address.addressNumber.toString(),
                })) || []),
              ]}
              placeholder={filterByAddressPlaceholder}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default OrdersListingFilters;
